import { Dialog, FormControl, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getImageSet, numberFormat, onKeyPressSubmit } from 'Util/DataUtils';
import icClose from 'Assets/img/recruitment/ic_close.png';
import icClose2 from 'Assets/img/recruitment/ic_close@2x.png';
import icClose3 from 'Assets/img/recruitment/ic_close@3x.png';
import icVisibility from 'Assets/img/recruitment/ic_visibility.png';
import icVisibility2 from 'Assets/img/recruitment/ic_visibility@2x.png';
import icVisibility3 from 'Assets/img/recruitment/ic_visibility@3x.png';
import icVisibilityOff from 'Assets/img/recruitment/ic_visibility_off.png';
import icVisibilityOff2 from 'Assets/img/recruitment/ic_visibility_off@2x.png';
import icVisibilityOff3 from 'Assets/img/recruitment/ic_visibility_off@3x.png';
import { useIntl } from 'react-intl';
import IntlMessages from 'Util/IntlMessages';
import CancelIcon from '@mui/icons-material/Cancel';
import { defaultError, loginError } from 'Api/Errors';
import LoadingView from 'Components/common/LoadingView';
import { login, me } from 'Api/endpoints/Auth';
import { signInFromRecruitment, updateMe } from 'Actions/AuthActions';
import { useDispatch } from 'react-redux';
import { useError } from 'Api/hooks/useError';

const customFieldStyle = {
  '& .MuiOutlinedInput-root': {
    fieldset: {
      border: '1px solid #C4C4C4',
    },
    '&:hover fieldset': {
      borderColor: '#C4C4C4',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #C4C4C4',
    },
    '&.Mui-error fieldset': {
      border: '1px solid #f00',
    },
  },
};
const customFieldInputStyle = {
  padding: '12px 10px',
  borderRadius: '6px',
  fontSize: '16px',
  fontFamily: 'Pretendard',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
};
const closeButtons = getImageSet([icClose, icClose2, icClose3]);
const visibility = getImageSet([icVisibility, icVisibility2, icVisibility3]);
const visibilityOff = getImageSet([icVisibilityOff, icVisibilityOff2, icVisibilityOff3]);

const LoginPopup = ({ open, onClose, onConfirm, onPopupJoin }) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { showErrorPopup } = useError();

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      setPhone('');
      setPassword('');
      setShowPassword(false);
      setErrorMessage('');
      setLoading(false);
    }
  }, [open]);

  const onChange = ({ target: { name, value: prevValue } }) => {
    let value = prevValue;
    if (name === 'phone') {
      value = numberFormat(prevValue, false, false, false);
      setPhone(value);
    } else {
      setPassword(value);
    }
    setErrorMessage('');
  };

  const getErrorMessage = type => {
    if (type === '') return '';
    if (!loginError[type]) return defaultError;
    return loginError[type];
  };

  const onClickLogin = async () => {
    if (phone === '' || password === '') {
      return null;
    }
    const data = { phone, phoneCountry: '82', password };
    try {
      setLoading(true);
      const result = await login(data);
      if (result.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }
      await dispatch(signInFromRecruitment(result.data));
      const meResult = await me();
      if (meResult.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }
      await dispatch(updateMe(meResult.data));
      onConfirm();
      onClose();
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onClickJoin = () => {
    onClose();
    onPopupJoin();
  };

  return (
    <Dialog fullWidth={true} maxWidth="basic" onClose={null} open={open}>
      <div className="recruitment-login-popup">
        <div className="recruitment-login-popup-header">
          <div style={{ width: '32px' }} />
          <div className="header-title">
            <IntlMessages id="sign.title" />
          </div>
          <div className="close-button">
            <img {...closeButtons} alt="close" onClick={onClose} />
          </div>
        </div>
        <div className="recruitment-login-popup-body">
          <FormControl className="id-input-wrapper" sx={customFieldStyle}>
            <OutlinedInput
              name="phone"
              value={phone}
              onChange={onChange}
              type="text"
              variant="outlined"
              placeholder={formatMessage({ id: 'recruitment.phone.placeholder' })}
              inputProps={{ style: customFieldInputStyle, tabIndex: '1' }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setPhone('')} edge="end">
                    {<CancelIcon sx={{ color: '#7a7a7a', fontSize: 24 }} />}
                  </IconButton>
                </InputAdornment>
              }
              autoFocus
            />
          </FormControl>
          <FormControl className="password-input-wrapper" sx={customFieldStyle}>
            <OutlinedInput
              autoComplete="new-password"
              name="password"
              value={password}
              onChange={onChange}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              placeholder={formatMessage({ id: 'common.password' })}
              inputProps={{ style: customFieldInputStyle, tabIndex: '2' }}
              onKeyPress={e => onKeyPressSubmit(e, onClickLogin)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(x => !x)} edge="end">
                    {showPassword ? <img {...visibilityOff} alt="visibilityOff" /> : <img {...visibility} alt="visibility" />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          <div className="error-view">{getErrorMessage(errorMessage) && <IntlMessages id={getErrorMessage(errorMessage)} />}</div>
        </div>
        <div className="recruitment-login-popup-footer">
          <div className="button-login" onClick={onClickLogin}>
            <IntlMessages id="sign.title" />
          </div>
          <div className="button-join-wrapper">
            <div className="button-join" onClick={onClickJoin}>
              <IntlMessages id="sign.join" />
            </div>
          </div>
        </div>
      </div>
      <LoadingView isLoading={loading} />
    </Dialog>
  );
};

export default LoginPopup;
